import {
  Checkbox,
  Loading,
  Pagination,
  Search,
  Select,
  SelectItem,
  Tab,
  Tabs,
} from 'carbon-components-react';
import './daily-fee.styles.scss';
import useSWR, { mutate } from 'swr';
import CustomButton from '../../components/custom-button/custom-btn.component';
import { useEffect, useState } from 'react';
import { useCustomMutation } from '../../utils/useCustomMutation';
import { postMethod } from '../../api/common';
import { useParams } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import Breadcrumbs from '../../components/breadcrumbs/Breadcrumbs';
import { Printer } from 'react-feather';
import { PrintView } from '../../components/PrintView/print-view.component';
import PrintOutHead from '../../components/PrintOutHead';
import { useDebounceState } from '../../utils/useDebounceState';
import { getQueryString } from '../../utils/common.util';
import { useClassAccessList } from '../../api/class.service';
import { useUrlState } from '../../utils/useUrlState';

const initialData = {
  searchTerm: '',
  classId: '',
};

export default function GroupUnderFeeTypePage() {
  const { feeTypeId, groupId } = useParams();

  const singleFeeTypeUrl = `daily-billing/fees/${feeTypeId}/summary`;
  const { data: d, isLoading: isFeeTyping } = useSWR(singleFeeTypeUrl);
  const feeType = d?.dailyBillingFee ?? {};

  const group = feeType?.genericAmounts?.find((c) => c.id === Number(groupId));

  const [query, setQuery] = useUrlState(initialData);

  const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebounceState(
    query.searchTerm,
  );

  useEffect(() => {
    setDebouncedSearchTerm(query.searchTerm);
  }, [query.searchTerm, setDebouncedSearchTerm]);

  const { classAccessList: classes } = useClassAccessList();

  const [payingSearchTerm, setPayingSearchTerm] = useState('');
  const [debouncedPayingSearchTerm, setDebouncedPayingSearchTerm] =
    useDebounceState('');
  const [payingClassId, setPayingClassId] = useState('');

  useEffect(() => {
    setDebouncedPayingSearchTerm(payingSearchTerm);
  }, [payingSearchTerm, setDebouncedPayingSearchTerm]);

  const [payingPaginate, setPayingPaginate] = useState({
    page: 1,
    pageSize: 50,
  });
  const [nonPayingPaginate, setNonPayingPaginate] = useState({
    page: 1,
    pageSize: 50,
  });

  // Jump back to page 1 whenever a filter changes
  useEffect(() => {
    setPayingPaginate((p) => ({ ...p, page: 1 }));
  }, [debouncedPayingSearchTerm, payingClassId]);
  useEffect(() => {
    setNonPayingPaginate((p) => ({ ...p, page: 1 }));
  }, [debouncedSearchTerm, query.classId]);

  // Students assigned to THIS group (server-side: Enrolled only, paged, searchable).
  const payingStudentsUrl = feeType?.id
    ? `daily-billing/fees/assigned-students?${getQueryString({
        dailyBillingFeeId: feeType.id,
        genericGroupingId: groupId,
        assignedToDailyBillingFee: true,
        query: debouncedPayingSearchTerm,
        classId: payingClassId,
        skip: payingPaginate.page - 1,
        take: payingPaginate.pageSize,
      })}`
    : null;
  const { data: payingData, isLoading: isPayingLoading } =
    useSWR(payingStudentsUrl);
  const payingStudents = payingData?.response ?? [];
  const payingTotal = payingData?.paginationInfo?.totalCount ?? 0;

  // Students not assigned to ANY group under this fee (server-side, paged).
  const nonPayingStudentsUrl = feeType?.id
    ? `daily-billing/fees/assigned-students?${getQueryString({
        dailyBillingFeeId: feeType.id,
        assignedToDailyBillingFee: false,
        query: debouncedSearchTerm,
        classId: query.classId,
        skip: nonPayingPaginate.page - 1,
        take: nonPayingPaginate.pageSize,
      })}`
    : null;
  const { data: nonPayingData, isLoading: isStudenting } =
    useSWR(nonPayingStudentsUrl);
  const nonPayingStudents = nonPayingData?.response ?? [];
  const nonPayingTotal = nonPayingData?.paginationInfo?.totalCount ?? 0;

  const isLoading = isFeeTyping || isStudenting;

  const [studentsToAdd, setStudentsToAdd] = useState([]);
  const [studentsToRemove, setStudentsToRemove] = useState([]);

  function selectStudentToAdd(studentId) {
    if (studentsToAdd.includes(studentId)) {
      setStudentsToAdd(studentsToAdd.filter((id) => id !== studentId));
    } else {
      setStudentsToAdd([...studentsToAdd, studentId]);
    }
  }
  function selectStudentToRemove(studentId) {
    if (studentsToRemove.includes(studentId)) {
      setStudentsToRemove(studentsToRemove.filter((id) => id !== studentId));
    } else {
      setStudentsToRemove([...studentsToRemove, studentId]);
    }
  }

  function refreshStudentLists() {
    mutate(singleFeeTypeUrl);
    if (payingStudentsUrl) mutate(payingStudentsUrl);
    if (nonPayingStudentsUrl) mutate(nonPayingStudentsUrl);
  }

  const {
    trigger: addStudents,
    isMutating: isAdding,
    error: addError,
  } = useCustomMutation(
    `daily-billing/fees/${feeType?.id}/add-student`,
    (url, { arg }) => postMethod(url, arg),
  );

  const {
    trigger: removeStudents,
    isMutating: isRemoving,
    error: removeError,
  } = useCustomMutation(
    `daily-billing/fees/${feeType?.id}/remove-student`,
    (url, { arg }) => postMethod(url, arg),
  );

  return (
    <PageLayout isLoading={isFeeTyping}>
      <div className="fee-type-page">
        <Breadcrumbs
          items={[
            { label: 'Daily Fees', to: '/daily-fees' },
            { label: 'Fee Types', to: '/daily-fees/types' },
            {
              label: feeType?.name,
              to: `/daily-fees/group-types/${feeType?.id}`,
            },
            { label: group?.name, active: true },
          ]}
        />
        <hr className="grey-hr mt-210 mb-10" />

        <h1 className="h-20">Select Students for {group?.name}</h1>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <p className="text-16 font-bold">{group?.name}</p>
            <p className="text-14">
              {payingTotal} student{payingTotal === 1 ? '' : 's'}
            </p>
          </div>
          <button
            className="clean-btn text-primary text-12 font-bold pointer"
            onClick={() => window.print()}
          >
            <Printer size="12" strokeWidth="5px" /> &nbsp; Print
          </button>
        </div>

        <PrintOutHead
          title={`Students - ${feeType?.name}`}
          headings={[
            { label: 'Group', value: group?.name },
            { label: 'Total', value: payingTotal },
          ]}
        />

        <Tabs className="mt-20">
          <Tab id="tab-1" label="Paying Students">
            {/* PAYING STUDENTS  */}
            <PrintView>
              <div className="mt-10">
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '40px',
                  }}
                >
                  <Select
                    labelText="Class"
                    onChange={(e) => setPayingClassId(e.target.value)}
                    value={payingClassId}
                  >
                    <SelectItem value="" text="All" />
                    {classes?.map((term) => (
                      <SelectItem
                        key={term.id}
                        value={term.id}
                        text={term.description}
                      />
                    ))}
                  </Select>
                  <Search
                    id="search-paying-students"
                    name="search-paying-students"
                    className="mt-25"
                    labelText=""
                    value={payingSearchTerm}
                    onChange={(e) => setPayingSearchTerm(e.target.value)}
                    placeHolderText="Search students..."
                  />
                </div>
                <div className="d-flex-space-between mt-20">
                  <p className="text-14">Students Under {feeType?.name}</p>
                  <p className="text-14">Total: {payingTotal}</p>
                </div>

                {removeError && (
                  <p className="text-14 mt-10 text-danger no-print">
                    {removeError?.message}
                  </p>
                )}

                {isPayingLoading ? (
                  <div className="d-flex-center mt-20">
                    <Loading withOverlay={false} small={true} />
                  </div>
                ) : payingStudents?.length > 0 ? (
                  <>
                    <StudentsTable
                      students={payingStudents}
                      selectStudent={selectStudentToRemove}
                      selectedStudents={studentsToRemove}
                      setSelectedStudents={setStudentsToRemove}
                      idPrefix="remove"
                    />
                    <div className="no-print">
                      <Pagination
                        backwardText="Previous page"
                        forwardText="Next page"
                        page={payingPaginate.page}
                        pageNumberText="Page Number"
                        pageSize={payingPaginate.pageSize}
                        pageSizes={[10, 20, 30, 40, 50, 100]}
                        totalItems={payingTotal}
                        onChange={(value) => setPayingPaginate(value)}
                      />
                    </div>
                  </>
                ) : (
                  <p className="text-14 mt-10 no-print">
                    No students are currently paying for {group?.name}.
                  </p>
                )}

                <div className="mt-40" style={{ display: 'flex' }}>
                  <div style={{ marginLeft: 'auto' }}>
                    <CustomButton
                      type="submit"
                      style={{ flex: '1' }}
                      classes={`primary font-bold`}
                      disabled={isRemoving || studentsToRemove.length === 0}
                      onClick={() => {
                        removeStudents(
                          {
                            genericGroupingId: group?.id,
                            studentids: studentsToRemove,
                          },
                          {
                            onSuccess() {
                              refreshStudentLists();
                              setStudentsToRemove([]);
                            },
                          },
                        );
                      }}
                    >
                      {isRemoving ? (
                        <div className="d-flex-center">
                          <Loading withOverlay={false} small={true} />
                        </div>
                      ) : (
                        'Remove Students'
                      )}
                    </CustomButton>
                  </div>
                </div>
              </div>
            </PrintView>
          </Tab>
          <Tab id="tab-4" label="Non-Paying Students">
            {/* UNASSIGNED STUDENTS */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '40px',
              }}
            >
              <Select
                labelText="Class"
                onChange={(e) => setQuery({ classId: e.target.value })}
                value={query.classId}
              >
                <SelectItem value="" text="All" />
                {classes?.map((term) => (
                  <SelectItem
                    key={term.id}
                    value={term.id}
                    text={term.description}
                  />
                ))}
              </Select>
              <Search
                id="search"
                name="search"
                className="mt-25"
                labelText=""
                value={query.searchTerm}
                onChange={(e) => setQuery({ searchTerm: e.target.value })}
                placeHolderText="Search for students..."
              />
            </div>
            <div className="mt-20 fade-in" hidden={!isLoading}>
              <Loading
                small={true}
                withOverlay={false}
                description="Loading..."
              />
            </div>

            <PrintView>
              {nonPayingStudents?.length > 0 && (
                <div className="mt-20">
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                  >
                    <p className="text-14">
                      Students Not Under {feeType?.name}
                    </p>
                    <p className="text-14">Total: {nonPayingTotal}</p>
                  </div>

                  <StudentsTable
                    students={nonPayingStudents}
                    selectStudent={selectStudentToAdd}
                    selectedStudents={studentsToAdd}
                    setSelectedStudents={setStudentsToAdd}
                    idPrefix="add"
                  />
                  <div className="no-print">
                    <Pagination
                      backwardText="Previous page"
                      forwardText="Next page"
                      page={nonPayingPaginate.page}
                      pageNumberText="Page Number"
                      pageSize={nonPayingPaginate.pageSize}
                      pageSizes={[10, 20, 30, 40, 50, 100]}
                      totalItems={nonPayingTotal}
                      onChange={(value) => setNonPayingPaginate(value)}
                    />
                  </div>

                  {addError && (
                    <p className="text-14 mt-10 text-danger no-print">
                      {addError?.message}
                    </p>
                  )}

                  <div className="mt-40" style={{ display: 'flex' }}>
                    <div style={{ marginLeft: 'auto' }}>
                      <CustomButton
                        type="submit"
                        style={{ flex: '1' }}
                        classes={`primary font-bold`}
                        disabled={isAdding || studentsToAdd.length === 0}
                        onClick={() => {
                          addStudents(
                            {
                              genericGroupingId: group?.id,
                              studentids: studentsToAdd,
                            },
                            {
                              onSuccess() {
                                refreshStudentLists();
                                setStudentsToAdd([]);
                              },
                            },
                          );
                        }}
                      >
                        {isAdding ? (
                          <div className="d-flex-center">
                            <Loading withOverlay={false} small={true} />
                          </div>
                        ) : (
                          'Add Students'
                        )}
                      </CustomButton>
                    </div>
                  </div>
                </div>
              )}
              {!isStudenting && nonPayingStudents?.length === 0 && (
                <p className="text-14 mt-10 no-print">
                  No students to add to {group?.name}.
                </p>
              )}
            </PrintView>
          </Tab>
        </Tabs>
      </div>
    </PageLayout>
  );
}

function StudentsTable({
  students,
  selectStudent,
  selectedStudents,
  setSelectedStudents,
  idPrefix = '',
}) {
  return (
    <div className="table-container">
      <table className="bx--data-table table-white">
        <thead>
          <tr>
            <th className="no-print">
              <Checkbox
                id={`select-all-students-${idPrefix}`}
                name={`select-all-students-${idPrefix}`}
                labelText=""
                checked={
                  students.length > 0 &&
                  students.every((student) =>
                    selectedStudents.includes(student.id),
                  )
                }
                indeterminate={
                  selectedStudents.length > 0 &&
                  !students.every((student) =>
                    selectedStudents.includes(student.id),
                  )
                }
                onChange={() => {
                  const pageIds = students.map((student) => student.id);
                  const allSelected = pageIds.every((id) =>
                    selectedStudents.includes(id),
                  );
                  if (allSelected) {
                    setSelectedStudents(
                      selectedStudents.filter((id) => !pageIds.includes(id)),
                    );
                  } else {
                    setSelectedStudents([
                      ...new Set([...selectedStudents, ...pageIds]),
                    ]);
                  }
                }}
              />
            </th>
            <th>Student ID</th>
            <th>First Name</th>
            <th>Last Name</th>
            <th>Gender</th>
            <th>Class</th>
          </tr>
        </thead>
        <tbody>
          {students?.map((student) => (
            <tr key={student.id}>
              <td className="no-print">
                <Checkbox
                  id={`student-${student.id}`}
                  name={`student-${student.id}`}
                  labelText=""
                  checked={selectedStudents.includes(student.id)}
                  onChange={() => selectStudent(student.id)}
                />
              </td>
              <td>
                {student.schoolStudentId
                  ? student.schoolStudentId
                  : student.studentId}
              </td>
              <td>{student.firstName}</td>
              <td>{student.lastName}</td>
              <td>{student.gender}</td>
              <td>{student.classDescription}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
